import { useSubdomain } from 'hooks/useSubdomain';
import React, { FunctionComponent, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'hooks/redux';
import {
  fetchNetworkForChannel,
  NetworkChannel
} from 'features/channel/channelSlice';
import { ChannelNetworkListLink } from './ChannelNetworkListLink';
import { ThumbImage } from 'components/Images/ThumbImage';
import { Loader } from 'components/Loader';
import { LogoImage } from 'components/LogoImage';
import { Icon } from 'components/Icons';

interface ChannelNetworkListProps {
  renderAsGrid?: boolean;
}

export const ChannelNetworkList: FunctionComponent<ChannelNetworkListProps> = (
  props
) => {
  const dispatch = useDispatch();
  const subdomain = useSubdomain();
  const network = useAppSelector((state) => state.channel.network);
  const networkIsLoaded = useAppSelector(
    (state) => state.channel.isNetworkLoaded
  );

  useEffect(
    function getNetwork() {
      if (subdomain) {
        dispatch(fetchNetworkForChannel(subdomain));
      }
    },
    [dispatch, subdomain]
  );

  if (!networkIsLoaded) {
    return <Loader fillComponent={true} />;
  }

  if (!network.length) {
    return <p>No other channels found in network.</p>;
  }

  return (
    <NetworkChannelList channels={network} renderAsGrid={props.renderAsGrid} />
  );
};

interface NetworkChannelListProps {
  channels: NetworkChannel[];
  renderAsGrid?: boolean;
}

// Live channels first. Shared by a channel's network list and a network's own page.
export const NetworkChannelList: FunctionComponent<NetworkChannelListProps> = ({
  channels,
  renderAsGrid
}) => {
  return (
    <ul
      className={`tw-m-0 tw-p-0 tw-list-none ${
        renderAsGrid ? 'tw-flex tw-flex-wrap tw-justify-center' : 'tw-space-y-4'
      }`}
    >
      {[channels.filter((c) => c.isLive), channels.filter((c) => !c.isLive)]
        .flat()
        .map((channel) => (
          <li
            key={channel.channelId}
            className={`${
              renderAsGrid
                ? 'tw-w-1/2 md:tw-w-1/3 lg:tw-w-1/4 tw-px-2 md:tw-px-4 tw-pb-4 md:tw-pb-8'
                : 'tw-flex tw-gap-2 tw-items-center'
            }`}
          >
            <ChannelNetworkListLink channel={channel}>
              <div
                className={`tw-flex tw-gap-2 tw-items-center tw-w-full ${
                  renderAsGrid ? 'tw-flex-col' : 'tw-flex-row'
                }`}
              >
                <div
                  className={`tw-mx-auto tw-relative ${
                    renderAsGrid
                      ? 'tw-h-[92px] tw-w-[92px]'
                      : 'tw-h-[44px] tw-w-[44px]'
                  }`}
                >
                  <LogoImage
                    size={renderAsGrid ? 'large' : 'small'}
                    hasFallback={!channel.logoUrl}
                  >
                    <ThumbImage
                      artworkUrl={channel.logoUrl}
                      artworkMode={'logo'}
                      alt={`Logo for ${channel.username}`}
                    />
                  </LogoImage>
                  {channel.isLive && (
                    <div className="tw-bg-mixlr-red tw-border-2 tw-border-solid tw-border-mixlr-label tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-h-[24px] tw-w-[24px] tw-absolute tw-top-0 tw-left-0">
                      <Icon
                        hidden={true}
                        icon={'liveNow'}
                        label={'Live'}
                        height={12}
                        width={12}
                      />
                    </div>
                  )}
                </div>
                {channel.username}
              </div>
            </ChannelNetworkListLink>
          </li>
        ))}
    </ul>
  );
};
